import React, { useEffect, useState, lazy, Suspense, Fragment } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import ExecutionEnvironment from "exenv";

// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";
import EditFoundingPartnerProfileModal from "../components/FoundingPartners/EditFoundingPartnerProfileModal";
import BlogSubmissionModal from "../components/FoundingPartners/BlogSubmissionModal";
import NominateFoundingPartnerModal from "../components/FoundingPartners/NominateFoundingPartnerModal";
import config from "../utils/config";

const LoginOrSignup = lazy(() =>
  import(
    /* webpackChunkName: "LoginOrSignup" */ "../components/Login/LoginOrSignup"
  )
);
const AffiliateDetails = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateDetails" */ "../components/Affiliate/AffiliateDetails"
  )
);
const AffiliateStats = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateStats" */ "../components/Affiliate/AffiliateStats"
  )
);
const AffiliateSales = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateSales" */ "../components/Affiliate/AffiliateSales"
  )
);
const AffiliatePayouts = lazy(() =>
  import(
    /* webpackChunkName: "AffiliatePayouts" */ "../components/Affiliate/AffiliatePayouts"
  )
);

import { makeStyles } from "tss-react/mui";
import {
  Typography,
  CircularProgress,
  Paper,
  Box,
  Alert,
  Chip,
  Button,
  Tabs,
  Tab,
  LinearProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Link from "@mui/material/Link";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// redux stuff
import { connect } from "react-redux";
import { getFoundingPartnerData } from "../redux/actions/dataActions";

const useStyles = makeStyles()((theme) => ({
  fullPageHeight: {
    minHeight: "100vh",
  },
  heading: {
    paddingTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  subTitle: {
    padding: "0px 30px 0px 30px",
    textAlign: "center",
    fontSize: 20,
  },
  loadingDiv: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  gridContainer: {
    display: "flex",
    justifyContent: "center",
    padding: `0 ${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)}`,
  },
  paper: {
    padding: theme.spacing(2),
    flexGrow: 1,
  },
  gridRoot: {
    flexGrow: 1,
    padding: theme.spacing(1),
    maxWidth: 1237,
  },
  foundingPartnerBadge: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontWeight: "bold",
    fontSize: "1rem",
    padding: theme.spacing(1, 2),
  },
  benefitsBox: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
  },
  benefitItem: {
    marginBottom: theme.spacing(1),
  },
  nextStepsBox: {
    backgroundColor: "#dac6eb",
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  tabsContainer: {
    borderBottom: 1,
    borderColor: "divider",
    marginBottom: theme.spacing(2),
  },
  logoImage: {
    maxWidth: 100,
    maxHeight: 100,
    width: "100%",
    height: 200,
    objectFit: "contain",
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
  },
}));

function FoundingPartnersDashboard(props) {
  const { classes } = useStyles();
  const history = useHistory();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [blogSubmissionModalOpen, setBlogSubmissionModalOpen] = useState(false);
  const [nominateModalOpen, setNominateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (props.user.authenticated) {
      props.getFoundingPartnerData().catch((err) => {
        console.error("Not a founding partner or error loading data");
        // Redirect to regular affiliate page if not a founding partner
        history.push("/affiliate");
      });
    }
  }, [props.user.authenticated]);

  const handleProfileModalClose = (updated) => {
    setProfileModalOpen(false);
    if (updated) {
      // Data is automatically refreshed by the action
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const calculateUniqueSubscriptions = (affiliateSales) => {
    const uniqueSubscriptions = new Set();
    affiliateSales.forEach((sale) => {
      // Only count initial sales (not renewals) and exclude refunded sales
      const isRefunded = sale.planName && sale.planName.includes("(Refunded)");
      const isRenewal = sale.paymentType === "renewalSubscriptionPayment";

      if (sale.subscriptionID && !isRenewal && !isRefunded) {
        uniqueSubscriptions.add(sale.subscriptionID);
      }
    });
    return uniqueSubscriptions.size;
  };

  const getBonusProgress = (uniqueSubscriptions) => {
    const milestones = [
      { count: 20, bonus: 250 },
      { count: 50, bonus: 750 },
      { count: 100, bonus: 2000 },
    ];

    let nextMilestone = null;
    let completedBonuses = [];

    milestones.forEach((milestone) => {
      if (uniqueSubscriptions >= milestone.count) {
        completedBonuses.push(milestone);
      } else if (!nextMilestone) {
        nextMilestone = milestone;
      }
    });

    return { nextMilestone, completedBonuses };
  };

  function getDashboardMarkup() {
    if (props.dataLoading || props.userLoading) {
      return (
        <div className={classes.loadingDiv}>
          <CircularProgress size={25} />
        </div>
      );
    } else if (
      props.user.authenticated &&
      typeof props.data.affiliateData.affiliateProfile !== "undefined" &&
      props.data.affiliateData.affiliateProfile.isFoundingPartner
    ) {
      const affiliateProfile = props.data.affiliateData.affiliateProfile;
      const foundingPartnerDetails =
        props.data.affiliateData.foundingPartnerDetails || {};

      return (
        <Fragment>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab label="General" />
              <Tab label="Marketing Materials" />
              <Tab label="Affiliate Sales" />
            </Tabs>
          </Box>

          {/* Tab Panel 0: General */}
          {activeTab === 0 && (
            <Fragment>
              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <Typography variant="h2">
                      Welcome, {foundingPartnerDetails.firstName}!
                    </Typography>
                    <Chip
                      icon={<StarIcon />}
                      label="FOUNDING PARTNER"
                      className={classes.foundingPartnerBadge}
                    />
                  </Box>
                  <Typography variant="body1" style={{ marginTop: 16 }}>
                    Thank you for being part of our exclusive Founding Partner
                    program. You're helping shape the future of Content
                    Generator!
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Typography variant="h3" gutterBottom>
                    Your Founding Partner Benefits
                  </Typography>
                  <div className={classes.benefitsBox}>
                    <Typography className={classes.benefitItem}>
                      ✅ <strong>40% Lifetime Recurring Commission</strong> -
                      Earn on every signup and renewal, forever
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      ✅ <strong>Free Lifetime Access</strong> - Full platform
                      access at no cost
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      ✅ <strong>Private Product Council</strong> - Direct input
                      on features and roadmap
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      ✅ <strong>Co-Branded Partner Profile</strong> - Your
                      brand featured on our platform
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      ✅ <strong>Performance Bonuses</strong> - 20 subscribers:
                      $250 | 50 subscribers: $750 | 100 subscribers: $2,000
                    </Typography>
                  </div>

                  <div className={classes.nextStepsBox}>
                    <Typography variant="h3" gutterBottom>
                      Next Steps
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      1.{" "}
                      <strong>Join the Private Discord Product Council</strong>{" "}
                      -{" "}
                      <Button
                        href="https://discord.gg/2Z9GcS3J"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: "white",
                          color: "#000",
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        Join here
                      </Button>
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      2. <strong>Vote on Features and Roadmap</strong> -{" "}
                      <Button
                        href="/feature-roadmap"
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: "white",
                          color: "#000",
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        Vote here
                      </Button>
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      2. <strong>Share Your Affiliate Link</strong> - See your
                      unique link below
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      3. <strong>Set Up Payouts</strong> - Add your Wise tag
                      below to receive payments
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      4. <strong>Start Promoting</strong> - Share Content
                      Generator with your audience
                    </Typography>
                  </div>
                </Paper>
              </Grid>

              {/* Nominate Founding Partner Section */}
              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Typography variant="h3" gutterBottom>
                    Nominate Someone to Be a Founding Partner
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Know someone who would make an excellent Founding Partner?
                    We're looking for people who are in the arena—actively
                    running a business, sharing their journey publicly, and
                    making content (such as posts, videos, or case studies) to
                    spread the message. If you know someone who fits this
                    description and has or is willing to create a public
                    profile, nominate them below!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setNominateModalOpen(true)}
                  >
                    Nominate a Founding Partner
                  </Button>
                </Paper>
              </Grid>

              {/* Public Profile Section */}
              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Typography variant="h3" gutterBottom>
                    Your Public Profile
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Update your public profile to be featured on our{" "}
                    <Link href="/founding-partners" target="_blank">
                      Founding Partners
                    </Link>{" "}
                    showcase page. Share your story, brand, and connect with the
                    community!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setProfileModalOpen(true)}
                  >
                    Update Your Public Profile
                  </Button>
                </Paper>
              </Grid>

              {/* Blog Post & Case Study Submissions Section */}
              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Typography variant="h3" gutterBottom>
                    Blog Post & Case Study Collaborations
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Share your expertise and success stories with our audience!
                    As a Founding Partner, you can submit blog posts and case
                    studies to be published on the ContentGenerator.io blog.
                  </Typography>
                  <div className={classes.benefitsBox}>
                    <Typography className={classes.benefitItem}>
                      📝 <strong>Minimum 1,000 words</strong> - Share valuable,
                      in-depth content
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      🔗 <strong>Link back to your website</strong> - Drive
                      traffic and build authority
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      ✍️ <strong>We reserve the right to edit</strong> - For SEO
                      and blog format consistency
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      ⚡ <strong>Quick review process</strong> - Get published
                      after a short review
                    </Typography>
                  </div>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setBlogSubmissionModalOpen(true)}
                  >
                    Submit Blog Post or Case Study
                  </Button>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Typography variant="h3" gutterBottom>
                    Resources & Support
                  </Typography>
                  <Typography className={classes.benefitItem}>
                    • <strong>Affiliate Agreement:</strong>{" "}
                    <Link
                      href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/affiliate-agreement.pdf?alt=media`}
                      target="_blank"
                      underline="hover"
                    >
                      View Agreement
                    </Link>
                  </Typography>
                  <Typography className={classes.benefitItem}>
                    • <strong>Need Help?</strong> Reach out to{" "}
                    <a
                      href="https://contentgenerator.tawk.help/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      support
                    </a>
                  </Typography>
                  <Typography className={classes.benefitItem}>
                    • <strong>Cookie Duration:</strong> 30 days
                  </Typography>
                  <Typography className={classes.benefitItem}>
                    • <strong>Minimum Payout:</strong> $100
                  </Typography>
                  <Typography className={classes.benefitItem}>
                    • <strong>Payment Schedule:</strong> Processed on or before
                    the 5th of each month (held 1 month for refunds)
                  </Typography>
                </Paper>
              </Grid>
            </Fragment>
          )}

          {/* Tab Panel 1: Marketing Materials */}
          {activeTab === 1 && (
            <Fragment>
              {/* Marketing Materials & Assets Section */}
              <Grid size={{ xs: 12 }}>
                <Paper className={classes.paper}>
                  <Typography variant="h3" gutterBottom>
                    Marketing Materials & Promotional Assets
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Use these professionally designed assets to promote Content
                    Generator to your audience. All materials are optimized for
                    social media and include your affiliate link.
                  </Typography>

                  <Typography
                    variant="h4"
                    gutterBottom
                    style={{ marginTop: 24 }}
                  >
                    Logos & Brand Assets
                  </Typography>
                  <div className={classes.benefitsBox}>
                    <Typography variant="body1" paragraph>
                      Download our official logos in various formats for use in
                      your content:
                    </Typography>
                    <Grid container spacing={2} mb={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Flogos%2FlogoOnWhite-200px.png?alt=media`}
                          alt="Logo on White"
                          className={classes.logoImage}
                          sx={{ backgroundColor: "white" }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Flogos%2FlogoOnWhite-200px.png?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download PNG
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Flogos%2Flogo.svg?alt=media`}
                          alt="Logo SVG"
                          className={classes.logoImage}
                          sx={{ backgroundColor: "white" }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Flogos%2Flogo.svg?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download SVG
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Flogos%2Flogo512.webp?alt=media`}
                          alt="Logo 512"
                          className={classes.logoImage}
                          sx={{ backgroundColor: "white" }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Flogos%2Flogo512.webp?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download WebP
                        </Button>
                      </Grid>
                    </Grid>
                  </div>

                  <Typography
                    variant="h4"
                    gutterBottom
                    style={{ marginTop: 24 }}
                  >
                    Social Media Banners
                  </Typography>
                  <div className={classes.benefitsBox}>
                    <Typography variant="body1" paragraph>
                      Eye-catching banners perfect for social media posts,
                      stories, and ads:
                    </Typography>
                    <Grid container spacing={2} mb={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-1.jpg?alt=media`}
                          alt="Turn URLs Into Ready-To-Post Content"
                          sx={{ width: "100%", borderRadius: 1, mb: 1 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-1.jpg?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-2.jpg?alt=media`}
                          alt="Let AI Handle Your Posting Routine"
                          sx={{ width: "100%", borderRadius: 1, mb: 1 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-2.jpg?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-3.jpg?alt=media`}
                          alt="AI That Writes, Designs & Schedules For You"
                          sx={{ width: "100%", borderRadius: 1, mb: 1 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-3.jpg?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box
                          component="img"
                          src={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-4.jpg?alt=media`}
                          alt="Smarter Content. Less Effort"
                          sx={{ width: "100%", borderRadius: 1, mb: 1 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/marketing-assets%2Fbanners%2Fmarketing-image-4.jpg?alt=media`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </Button>
                      </Grid>
                    </Grid>
                  </div>

                  <Typography
                    variant="h4"
                    gutterBottom
                    style={{ marginTop: 24 }}
                  >
                    Example Post Copy
                  </Typography>
                  <div className={classes.benefitsBox}>
                    <Typography variant="body1" paragraph>
                      Use these proven post templates (remember to add your
                      affiliate link!):
                    </Typography>
                    <Box
                      mb={2}
                      p={2}
                      sx={{ backgroundColor: "white", borderRadius: 1 }}
                    >
                      <Typography variant="body2" paragraph>
                        <strong>Short & Sweet:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        "Tired of spending hours on social media content? 🤯
                        <br />
                        <br />
                        Content Generator turns any URL into ready-to-post
                        content in seconds. AI writes, designs, and schedules
                        everything for you.
                        <br />
                        <br />
                        10x faster. Way less effort. ⚡<br />
                        <br />
                        [Your Affiliate Link]"
                      </Typography>
                    </Box>
                    <Box
                      mb={2}
                      p={2}
                      sx={{ backgroundColor: "white", borderRadius: 1 }}
                    >
                      <Typography variant="body2" paragraph>
                        <strong>Problem/Solution:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        "Here's what I used to do: Spend 2-3 hours creating
                        social media posts every week 😮‍💨
                        <br />
                        <br />
                        Here's what I do now: Drop a URL into Content Generator
                        and let AI handle the rest ✨<br />
                        <br />
                        It generates posts for Instagram, Facebook, LinkedIn, X,
                        and Pinterest - all optimized for each platform. Posts
                        are designed, written, and scheduled automatically.
                        <br />
                        <br />
                        If you're a business owner or content creator, this is a
                        game-changer.
                        <br />
                        <br />
                        Start free: [Your Affiliate Link]"
                      </Typography>
                    </Box>
                    <Box
                      mb={2}
                      p={2}
                      sx={{ backgroundColor: "white", borderRadius: 1 }}
                    >
                      <Typography variant="body2" paragraph>
                        <strong>Value Proposition:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        "What if you could automate your entire social media
                        posting routine? 🤔
                        <br />
                        <br />
                        That's exactly what Content Generator does:
                        <br />
                        • Turn any article/blog into social posts
                        <br />
                        • AI writes platform-optimized captions
                        <br />
                        • Auto-generates beautiful designs
                        <br />
                        • Schedules posts across all platforms
                        <br />
                        • Supports Pinterest, Instagram, Facebook, X, LinkedIn
                        <br />
                        <br />
                        Built for entrepreneurs and creators who want results
                        without the busy work.
                        <br />
                        <br />
                        Try it free: [Your Affiliate Link]"
                      </Typography>
                    </Box>
                  </div>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Pro Tip:</strong> Mix these assets with your own
                      content and personal stories for the best results. Share
                      your genuine experience with Content Generator!
                    </Typography>
                  </Alert>
                </Paper>
              </Grid>
            </Fragment>
          )}

          {/* Tab Panel 2: Affiliate Sales */}
          {activeTab === 2 && (
            <Fragment>
              <Grid container spacing={2}>
                {/* Performance Bonus Tracker */}
                <Grid size={{ xs: 12 }}>
                  <Paper className={classes.paper}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <EmojiEventsIcon color="primary" fontSize="large" />
                      <Typography variant="h3">
                        Performance Bonus Tracker
                      </Typography>
                    </Box>

                    {(() => {
                      const uniqueSubs = calculateUniqueSubscriptions(
                        props.data.affiliateData.affiliateSales || []
                      );
                      const { nextMilestone, completedBonuses } =
                        getBonusProgress(uniqueSubs);

                      return (
                        <>
                          <Typography variant="h4" gutterBottom>
                            Unique Subscriptions: <strong>{uniqueSubs}</strong>
                          </Typography>

                          {completedBonuses.length > 0 && (
                            <Box mb={3}>
                              <Alert severity="success">
                                <Typography variant="body1">
                                  🎉 <strong>Congratulations!</strong> You've
                                  earned {completedBonuses.length} bonus
                                  {completedBonuses.length > 1 ? "es" : ""}:
                                </Typography>
                                {completedBonuses.map((bonus, index) => (
                                  <Typography key={index} variant="body2">
                                    ✅ {bonus.count} subscribers - $
                                    {bonus.bonus} bonus earned
                                  </Typography>
                                ))}
                              </Alert>
                            </Box>
                          )}

                          {nextMilestone && (
                            <Box>
                              <Typography variant="body1" gutterBottom>
                                Next Milestone:{" "}
                                <strong>
                                  {nextMilestone.count} subscribers
                                </strong>{" "}
                                - ${nextMilestone.bonus} bonus
                              </Typography>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={2}
                                mb={1}
                              >
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    (uniqueSubs / nextMilestone.count) * 100
                                  }
                                  sx={{
                                    flexGrow: 1,
                                    height: 10,
                                    borderRadius: 5,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {uniqueSubs} / {nextMilestone.count}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {nextMilestone.count - uniqueSubs} more
                                subscriber
                                {nextMilestone.count - uniqueSubs !== 1
                                  ? "s"
                                  : ""}{" "}
                                to unlock ${nextMilestone.bonus}!
                              </Typography>
                            </Box>
                          )}

                          {!nextMilestone && uniqueSubs >= 100 && (
                            <Alert severity="success">
                              <Typography variant="body1">
                                🏆 <strong>Amazing!</strong> You've completed
                                all performance bonus milestones! Total bonuses
                                earned: $
                                {completedBonuses.reduce(
                                  (sum, b) => sum + b.bonus,
                                  0
                                )}
                              </Typography>
                            </Alert>
                          )}
                        </>
                      );
                    })()}
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <AffiliateDetails />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <AffiliateStats />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <AffiliateSales />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <AffiliatePayouts />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper className={classes.paper}>
                    <Typography variant="h3" gutterBottom>
                      Resources & Support
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      • <strong>Affiliate Agreement:</strong>{" "}
                      <Link
                        href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/affiliate-agreement.pdf?alt=media`}
                        target="_blank"
                        underline="hover"
                      >
                        View Agreement
                      </Link>
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      • <strong>Need Help?</strong> Reach out to{" "}
                      <a
                        href="https://contentgenerator.tawk.help/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        support
                      </a>
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      • <strong>Cookie Duration:</strong> 30 days
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      • <strong>Minimum Payout:</strong> $100
                    </Typography>
                    <Typography className={classes.benefitItem}>
                      • <strong>Payment Schedule:</strong> Processed on or
                      before the 5th of each month (held 1 month for refunds)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Fragment>
          )}
        </Fragment>
      );
    } else if (props.user.authenticated) {
      // User is authenticated but not a founding partner
      return (
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning">
            You don't have access to the Founding Partner program. If you
            believe this is an error, please contact support.
          </Alert>
        </Grid>
      );
    } else {
      // Not authenticated
      return (
        <Grid size={{ xs: 12 }}>
          <LoginOrSignup titleText="Log in to access your Founding Partner dashboard" />
        </Grid>
      );
    }
  }

  return (
    <div className={classes.fullPageHeight}>
      <CustomMeta
        title="Founding Partner Dashboard - Content Generator"
        description="Manage your Founding Partner account with exclusive 40% commission rates and benefits."
        url="https://contentgenerator.io/founding-partner-dashboard"
      />
      <div className={classes.heading}>
        <Typography variant="h1">Founding Partner Dashboard</Typography>
      </div>
      <Typography className={classes.subTitle}>
        Your exclusive partner portal with 40% lifetime commissions
      </Typography>
      <div className={classes.gridContainer}>
        {ExecutionEnvironment.canUseDOM && (
          <Suspense fallback={<div />}>
            <Grid container className={classes.gridRoot} spacing={2}>
              {getDashboardMarkup()}
            </Grid>
          </Suspense>
        )}
      </div>

      {/* Edit Profile Modal */}
      {props.user.authenticated &&
        props.data.affiliateData.foundingPartnerDetails && (
          <EditFoundingPartnerProfileModal
            open={profileModalOpen}
            onClose={handleProfileModalClose}
            foundingPartnerDetails={
              props.data.affiliateData.foundingPartnerDetails
            }
          />
        )}

      {/* Blog Submission Modal */}
      <BlogSubmissionModal
        open={blogSubmissionModalOpen}
        onClose={() => setBlogSubmissionModalOpen(false)}
      />

      {/* Nominate Founding Partner Modal */}
      <NominateFoundingPartnerModal
        open={nominateModalOpen}
        onClose={() => setNominateModalOpen(false)}
      />
    </div>
  );
}

FoundingPartnersDashboard.propTypes = {
  data: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  getFoundingPartnerData: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
  dataLoading: state.data.loading,
  userLoading: state.user.loading,
});

export default connect(mapStateToProps, { getFoundingPartnerData })(
  FoundingPartnersDashboard
);
